import axiosInstance from '../lib/axios';
import type { ApiResponse } from './api';

export type MarketplaceItemType = 'skill' | 'tool' | 'mcp_server';

export interface MarketplaceVersion {
  publicId: string;
  versionNumber: number;
  semver?: string | null;
  manifest: Record<string, unknown>;
  payload: Record<string, unknown>;
  riskLevel: string;
  validationStatus: string;
  validationFindings: unknown[];
  executionStatus: string;
  publicationStatus: string;
  createdAt?: string | null;
}

export interface MarketplaceItem {
  publicId: string;
  type: MarketplaceItemType;
  name: string;
  title: string;
  description?: string | null;
  visibility: string;
  executionStatus: string;
  publicationStatus: string;
  installCount: number;
  latestVersion?: MarketplaceVersion | null;
  activeExecutionVersion?: MarketplaceVersion | null;
  activePublicVersion?: MarketplaceVersion | null;
}

export interface MarketplaceInstallation {
  publicId: string;
  itemPublicId: string;
  installedVersionPublicId: string;
  status: string;
  autoUpdatePolicy: string;
}

export interface MarketplaceBinding {
  publicId: string;
  sourceType: string;
  itemPublicId?: string | null;
  installationPublicId?: string | null;
  targetType: string;
  targetId: string;
  enabled: boolean;
  pinnedVersionPublicId?: string | null;
}

export interface MarketplaceDomainVerification {
  publicId: string;
  domain: string;
  method: 'dns_txt' | 'http_well_known';
  status: string;
  verificationToken?: string;
  instructions?: {
    type: string;
    name?: string;
    value?: string;
    url?: string;
    body?: string;
  };
  verifiedAt?: string | null;
  expiresAt?: string | null;
}

export interface CreateMarketplaceItemInput {
  type: MarketplaceItemType;
  name: string;
  title: string;
  description?: string;
  visibility?: 'private' | 'public';
}

export interface CreateMarketplaceVersionInput {
  semver?: string;
  payload: Record<string, unknown>;
}

export async function listMarketplaceItems() {
  const response = await axiosInstance.get<ApiResponse<MarketplaceItem[]>>('/v1/marketplace/items');
  return response.data;
}

export async function listMarketplaceDomainVerifications() {
  const response = await axiosInstance.get<ApiResponse<MarketplaceDomainVerification[]>>(
    '/v1/marketplace/domain-verifications'
  );
  return response.data;
}

export async function createMarketplaceDomainVerification(input: {
  domain: string;
  method: 'dns_txt' | 'http_well_known';
}) {
  const response = await axiosInstance.post<ApiResponse<MarketplaceDomainVerification>>(
    '/v1/marketplace/domain-verifications',
    input
  );
  return response.data;
}

export async function verifyMarketplaceDomainVerification(verificationPublicId: string) {
  const response = await axiosInstance.post<ApiResponse<MarketplaceDomainVerification>>(
    `/v1/marketplace/domain-verifications/${verificationPublicId}/verify`,
    {}
  );
  return response.data;
}

export async function createMarketplaceItem(input: CreateMarketplaceItemInput) {
  const response = await axiosInstance.post<ApiResponse<MarketplaceItem>>(
    '/v1/marketplace/items',
    input
  );
  return response.data;
}

export async function createMarketplaceVersion(
  itemPublicId: string,
  input: CreateMarketplaceVersionInput
) {
  const response = await axiosInstance.post<ApiResponse<MarketplaceVersion>>(
    `/v1/marketplace/items/${itemPublicId}/versions`,
    input
  );
  return response.data;
}

export async function submitExecutionReview(versionPublicId: string) {
  const response = await axiosInstance.post<ApiResponse<MarketplaceVersion>>(
    `/v1/marketplace/versions/${versionPublicId}/submit-execution-review`
  );
  return response.data;
}

export async function submitPublicReview(itemPublicId: string, versionPublicId?: string) {
  const response = await axiosInstance.post<ApiResponse<MarketplaceVersion>>(
    `/v1/marketplace/items/${itemPublicId}/submit-public`,
    versionPublicId ? { version_public_id: versionPublicId } : {}
  );
  return response.data;
}

export async function browseMarketplacePublic() {
  const response =
    await axiosInstance.get<ApiResponse<MarketplaceItem[]>>('/v1/marketplace/public');
  return response.data;
}

export async function installMarketplaceItem(itemPublicId: string) {
  const response = await axiosInstance.post<ApiResponse<MarketplaceInstallation>>(
    `/v1/marketplace/public/${itemPublicId}/install`,
    {}
  );
  return response.data;
}

export async function listMarketplaceInstallations() {
  const response = await axiosInstance.get<ApiResponse<MarketplaceInstallation[]>>(
    '/v1/marketplace/installations'
  );
  return response.data;
}

export async function updateMarketplaceInstallation(
  installationPublicId: string,
  versionPublicId: string
) {
  const response = await axiosInstance.post<ApiResponse<MarketplaceInstallation>>(
    `/v1/marketplace/installations/${installationPublicId}/update`,
    { version_public_id: versionPublicId }
  );
  return response.data;
}

export async function bindMarketplaceItemToAgent(input: {
  agentPublicId: string;
  sourceType: 'owner_item' | 'public_installation' | 'platform_item';
  itemPublicId?: string;
  installationPublicId?: string;
}) {
  const response = await axiosInstance.post<ApiResponse<MarketplaceBinding>>(
    `/v1/agents/${input.agentPublicId}/marketplace-bindings`,
    {
      source_type: input.sourceType,
      item_public_id: input.itemPublicId,
      installation_public_id: input.installationPublicId,
    }
  );
  return response.data;
}
